"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { signInHref, type AuthPage } from "@/lib/post-login-redirect";

type Location = { path: string; rest: string };

/** `usePathname` and `window.location` may differ in percent-encoding only. */
function samePath(a: string, b: string): boolean {
  if (a === b) return true;
  try {
    return decodeURI(a) === decodeURI(b);
  } catch {
    return false;
  }
}

/**
 * The page the visitor is on, path plus query plus hash, for a sign-in link
 * that should bring them back to it (`RV-20`).
 *
 * The server and the first client render know only the path (`usePathname`);
 * `useSearchParams` would opt every statically rendered page that draws the
 * site bar out of static rendering. So the query and the hash are read from
 * `window.location` after each commit that changes the path (by then the
 * router has written the new address), and again on back, forward and a hash
 * change. A query the page rewrites in place without a navigation is picked
 * up at the next one.
 */
export function useReturnPath(): string {
  const pathname = usePathname() ?? "";
  const [location, setLocation] = useState<Location | null>(null);

  useEffect(() => {
    const read = () =>
      setLocation({ path: window.location.pathname, rest: `${window.location.search}${window.location.hash}` });
    read();
    window.addEventListener("popstate", read);
    window.addEventListener("hashchange", read);
    return () => {
      window.removeEventListener("popstate", read);
      window.removeEventListener("hashchange", read);
    };
  }, [pathname]);

  // Until the address is read, or while it still belongs to the page before.
  if (!location || !samePath(location.path, pathname)) return pathname;
  return `${location.path}${location.rest}`;
}

/** `signInHref` for the page the visitor is on. */
export function useSignInHref(locale: string, page: AuthPage = "login"): string {
  return signInHref(locale, useReturnPath(), page);
}
